import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import SlackConnectModal from '../slackAgent/components/SlackConnectModal';
import SlackIcon from '../resources/svg/icons/slack-icon.svg';
import Icon, { IconSizes, IconTypes, IconVariants } from '../components/foundations/Icon';
import { useConnectionsContext } from './connectionsContext';
import ConnectionsMenu from './components/ConnectionsMenu';
import {
    CONNECTION_EVENTS,
    CONNECTION_STATES,
    CONNECTION_SURFACES,
    SLACK_CONNECTION_ID,
    TRANSLATION_KEY,
    connectionEventProperties,
    descriptionKey,
    stateKey,
    trackConnectionEvent,
} from './constants';

const MetaLogo = ({ className }) => (
    <Icon
        name="brand-meta"
        type={IconTypes.TABLER}
        size={IconSizes.MEDIUM}
        variant={IconVariants.ACTIVE}
        interactive={false}
        className={className}
    />
);

MetaLogo.propTypes = {
    className: PropTypes.string,
};

const CONNECTION_LOGOS = {
    meta: MetaLogo,
    [SLACK_CONNECTION_ID]: SlackIcon,
};

// Shown before a first connect, for connections whose consent screen needs explaining up front.
const CONNECTION_INTROS = {
    [SLACK_CONNECTION_ID]: SlackConnectModal,
};

const statusText = (connection, translate) => {
    const { state, display_name: name, account_name: accountName } = connection;
    if (state === CONNECTION_STATES.NOT_CONNECTED) return null;
    if (state === CONNECTION_STATES.CONNECTED && accountName)
        return translate(`${TRANSLATION_KEY}.STATES.CONNECTED_TO`, { accountName });
    return translate(stateKey(state), { name });
};

const ChatConnections = ({ translate }) => {
    const { connections, loadError, busyId, refresh, connect, disconnect } = useConnectionsContext();
    const [introId, setIntroId] = useState(null);

    const startConnect = connection => {
        const eventProperties = connectionEventProperties(connection, CONNECTION_SURFACES.HEADER);
        trackConnectionEvent(CONNECTION_EVENTS.CONNECT_CLICKED, eventProperties);
        if (connection.state === CONNECTION_STATES.NOT_CONNECTED && CONNECTION_INTROS[connection.id]) {
            trackConnectionEvent(CONNECTION_EVENTS.INTRO_OPENED, eventProperties);
            setIntroId(connection.id);
            return;
        }
        connect(connection.id, CONNECTION_SURFACES.HEADER);
    };

    const continueIntro = () => {
        const connection = connections.find(item => item.id === introId);
        trackConnectionEvent(
            CONNECTION_EVENTS.INTRO_CONTINUED,
            connectionEventProperties(connection, CONNECTION_SURFACES.HEADER)
        );
        connect(introId, CONNECTION_SURFACES.HEADER);
    };

    const cancelIntro = () => {
        const connection = connections.find(item => item.id === introId);
        trackConnectionEvent(
            CONNECTION_EVENTS.INTRO_CANCELLED,
            connectionEventProperties(connection, CONNECTION_SURFACES.HEADER)
        );
        setIntroId(null);
    };

    const items = connections
        .filter(connection => connection.available && CONNECTION_LOGOS[connection.id])
        .map(connection => ({
            id: connection.id,
            type: connection.type,
            name: connection.display_name,
            description: translate(descriptionKey(connection.type), { name: connection.display_name }),
            statusText: statusText(connection, translate),
            Logo: CONNECTION_LOGOS[connection.id],
            connected: connection.state === CONNECTION_STATES.CONNECTED,
            busy: busyId === connection.id,
            onConnect: () => startConnect(connection),
            onDisconnect: () => disconnect(connection.id, CONNECTION_SURFACES.HEADER),
        }));

    const Intro = introId && CONNECTION_INTROS[introId];

    return (
        <>
            <ConnectionsMenu items={items} loadError={Boolean(loadError)} onRetry={refresh} />
            {Intro && <Intro open busy={busyId === introId} onContinue={continueIntro} onCancel={cancelIntro} />}
        </>
    );
};

ChatConnections.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ChatConnections);
